"use client";
import { useEffect, useCallback } from "react";
import { useParams, usePathname } from "next/navigation";
import useCollection from "@/app/lib/features/collection/collection.hook";
import useFilters from "@/app/lib/features/filters/filters.hook";
import { trackInteraction } from "@/app/lib/tracking/interactions";
import useFilterUrlSync from "@/app/hooks/useFilterUrlSync";
import useDebounce from "@/app/hooks/useDebounce";
import {
  DEFAULT_FILTER_REQUEST,
  FilterRequest,
} from "@/app/lib/features/filters/filters.types";
import DynamicBreadcrumb from "@/app/components/elements/breadcrumb/DynamicBreadcrumb";
import ProductsLayout from "@/app/components/common/products/ProductsLayout";
import ProductsHeader from "@/app/components/common/products/ProductsHeader";
import GenericProductsPageSkeleton from "@/app/components/elements/skeletons/GenericProductsPageSkeleton";
import FallbackImage from "@/app/components/elements/FallbackImage";
import { Button } from "@/app/components/shadcn/button";
import NotFoundPage from "@/app/(pages)/not-found/NotFoundPage";
import { FolderHeart } from "lucide-react";

const BASE_FILTER_REQUEST: FilterRequest = {
  section_id: DEFAULT_FILTER_REQUEST.section_id,
  seque: DEFAULT_FILTER_REQUEST.seque,
};

const CollectionDetailClient = () => {
  const params = useParams();
  const uuid = params.uuid as string;

  const {
    selectedCollection,
    selectedCollectionItems,
    selectedCollectionPagination,
    isSelectedCollectionLoading,
    isSelectedCollectionSuccess,
    isSelectedCollectionError,
    hasMoreCollectionItems,
    fetchCollection,
    fetchMoreCollectionItems,
    clearSelectedCollection,
    toggleFollow,
    isTogglingFollow,
  } = useCollection();

  const {
    selected,
    updateSelected,
    fetchFilters,
    fetchGeneralFilters,
    reset: resetFilters,
    clearFilters,
    userInteractionCount,
  } = useFilters();

  const pathname = usePathname();
  const handleClearFilters = useCallback(() => {
    clearFilters();
    setTimeout(() => {
      window.history.replaceState(null, "", pathname);
    }, 0);
  }, [clearFilters, pathname]);

  const { activeFilterRequest, initReady } = useFilterUrlSync({
    selected,
    updateSelected,
    baseFilterRequest: BASE_FILTER_REQUEST,
    interactionCount: userInteractionCount,
  });

  const debouncedKey = useDebounce(JSON.stringify(activeFilterRequest), 500);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { section_id: _sid, seque: _seq, ...collectionFilters } = activeFilterRequest;

  useEffect(() => {
    if (!initReady || !uuid) return;
    fetchFilters(activeFilterRequest);
    fetchGeneralFilters({});
    fetchCollection(uuid, collectionFilters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedKey, uuid, initReady]);

  useEffect(() => {
    return () => {
      clearSelectedCollection();
      resetFilters();
    };
  }, [clearSelectedCollection, resetFilters]);

  useEffect(() => {
    if (selectedCollection?.id) trackInteraction("section", selectedCollection.id, "view");
  }, [selectedCollection?.id]);

  if (isSelectedCollectionError) {
    return (
      <NotFoundPage
        title="Koleksiyon Bulunamadı"
        description="Aradığınız koleksiyon mevcut değil veya kaldırılmış olabilir."
        icon={FolderHeart}
        primaryLabel="Tüm Koleksiyonlar"
        primaryHref="/koleksiyonlar"
        showHomeButton
      />
    );
  }

  const products = (selectedCollectionItems ?? []).map((item) => item.product).filter((p) => p?.id);
  const total = selectedCollectionPagination?.total ?? products.length;
  const collectionName = selectedCollection?.name ?? "";
  const isFollowed =
    selectedCollection?.is_followed ??
    !!selectedCollection?.button_list?.some((btn) => btn.action === "unfollow");

  if (isSelectedCollectionLoading && products.length === 0) {
    return <GenericProductsPageSkeleton />;
  }

  return (
    <div className="container-main">
      <DynamicBreadcrumb title={collectionName || "Koleksiyon"} />

      {selectedCollection && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 mb-2">
          <div className="flex items-center gap-3 min-w-0">
            <div className="relative w-10 h-10 shrink-0">
              <FallbackImage
                src={selectedCollection.customer_photo}
                alt={selectedCollection.customer_full_name}
                fill
                className="object-cover rounded-full border border-[#ffecdf]"
                sizes="40px"
              />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-base leading-tight">
                {collectionName}
              </p>
              <p className="text-sm text-muted-foreground">
                {selectedCollection.customer_full_name}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 sm:ml-auto text-xs text-muted-foreground shrink-0">
            <span>{selectedCollection.items_count_text}</span>
            <span>{selectedCollection.followers_count_text}</span>
            <span>{selectedCollection.views_count_text}</span>
            {!selectedCollection.is_collection_auth && (
              <Button
                size="sm"
                variant={isFollowed ? "default" : "outline"}
                disabled={isTogglingFollow}
                onClick={() => { trackInteraction("section", selectedCollection.id, "follow"); toggleFollow(selectedCollection.id); }}
                className="h-8 ml-auto sm:ml-0"
              >
                {isFollowed ? "Takip Ediliyor" : "Takip Et"}
              </Button>
            )}
          </div>
        </div>
      )}

      <ProductsLayout
        header={(onOpenFilter) => (
          <ProductsHeader
            title={collectionName}
            productCount={isSelectedCollectionSuccess && total > 0 ? total : undefined}
            onOpenFilter={onOpenFilter}
          />
        )}
        products={products}
        hasMore={hasMoreCollectionItems}
        isLoading={isSelectedCollectionLoading}
        isSuccess={isSelectedCollectionSuccess}
        onLoadMore={fetchMoreCollectionItems}
        onClearFilters={handleClearFilters}
      />
    </div>
  );
};

export default CollectionDetailClient;